import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiMessageSquare,
  FiMail,
  FiPhone,
  FiCalendar,
  FiUser,
  FiEdit,
  FiUserCheck,
  FiClock,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import adminApi from "../../api/adminApi";
import authApi from "../../api/authApi";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/Button/Button";
import Select from "../../components/Select/Select";
import FormInput from "../../components/FormInput/FormInput";
import "./EnquiryDetail.css";

const EnquiryDetail = () => {
  const { enquiryId } = useParams();
  const navigate = useNavigate();

  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staff, setStaff] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);

  // Form states
  const [statusLoading, setStatusLoading] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [assignedTo, setAssignedTo] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [priority, setPriority] = useState("medium");
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [convertStudentId, setConvertStudentId] = useState("");
  const [convertBatchId, setConvertBatchId] = useState("");
  const [convertLoading, setConvertLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState("");
  const [existingUser, setExistingUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(false);
  const [createUserMode, setCreateUserMode] = useState(false);
  const [newUserData, setNewUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobileNumber: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    termsAccepted: true, // Default to true for admin-created accounts
  });

  useEffect(() => {
    loadEnquiry();
    loadStaff();
    loadCourses();
    loadStudents();
  }, [enquiryId]);

  useEffect(() => {
    if (studentSearchQuery) {
      searchStudents();
    } else {
      loadStudents();
    }
  }, [studentSearchQuery]);

  useEffect(() => {
    if (enquiry?.courseInterest) {
      loadBatchesForCourse(
        enquiry.courseInterest._id || enquiry.courseInterest
      );
    }
  }, [enquiry?.courseInterest]);

  useEffect(() => {
    if (studentSearchQuery) {
      searchStudents();
    } else {
      loadStudents();
    }
  }, [studentSearchQuery]);

  // Check for existing user and load batches when convert modal opens
  useEffect(() => {
    if (showConvertModal && enquiry) {
      if (!existingUser && !checkingUser) {
        checkExistingUser();
      }
      // Load batches if not already loaded
      if (enquiry.courseInterest && batches.length === 0) {
        loadBatchesForCourse(
          enquiry.courseInterest._id || enquiry.courseInterest
        );
      }
    }
  }, [showConvertModal]);

  const loadEnquiry = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get(`/enquiries/${enquiryId}`);
      setEnquiry(response.data);
      setNewStatus(response.data.status || "new");
      setNotes(response.data.notes || "");
      setPriority(response.data.priority || "medium");
      setAssignedTo(
        response.data.assignedTo?._id || response.data.assignedTo || ""
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load enquiry");
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: { role: "mentor", limit: 1000 },
      });
      const usersList = response.data?.users || response.data?.data || [];
      setStaff(usersList);
    } catch (err) {
      console.error("Failed to load staff:", err);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await adminApi.get("/courses", {
        params: { limit: 1000 },
      });
      const coursesList = response.data?.courses || response.data?.data || [];
      setCourses(coursesList);
    } catch (err) {
      console.error("Failed to load courses:", err);
    }
  };

  const loadBatchesForCourse = async (courseId) => {
    try {
      const response = await adminApi.get("/batches", {
        params: { courseId, limit: 1000 },
      });
      const batchesList = response.data?.batches || response.data?.data || [];
      setBatches(
        batchesList.filter(
          (b) => b.status === "upcoming" || b.status === "ongoing"
        )
      );
    } catch (err) {
      console.error("Failed to load batches:", err);
    }
  };

  const checkExistingUser = async () => {
    if (!enquiry?.email && !enquiry?.phone) return;

    setCheckingUser(true);
    try {
      // Search for user by email or phone
      const searchQuery = enquiry.email || enquiry.phone;
      const response = await adminApi.get("/users", {
        params: {
          role: "student",
          search: searchQuery,
          limit: 10,
        },
      });
      const usersList = response.data?.users || response.data?.data || [];

      // Check if any user matches the enquiry email or phone exactly
      const matchedUser = usersList.find(
        (u) =>
          u.email?.toLowerCase() === enquiry.email?.toLowerCase() ||
          u.mobileNumber === enquiry.phone ||
          u.phone === enquiry.phone
      );

      if (matchedUser) {
        setExistingUser(matchedUser);
        setConvertStudentId(matchedUser._id);
      }
    } catch (err) {
      console.error("Failed to check existing user:", err);
    } finally {
      setCheckingUser(false);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: { role: "student", limit: 1000 },
      });
      const usersList = response.data?.users || response.data?.data || [];
      setStudents(usersList);
    } catch (err) {
      console.error("Failed to load students:", err);
    }
  };

  const searchStudents = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: {
          role: "student",
          search: studentSearchQuery,
          limit: 100,
        },
      });
      const usersList = response.data?.users || response.data?.data || [];
      setStudents(usersList);
    } catch (err) {
      console.error("Failed to search students:", err);
    }
  };

  const handleCreateUserFromEnquiry = async () => {
    // Client-side validation
    if (!newUserData.firstName || !newUserData.email || !newUserData.password) {
      setError(
        "Please fill in required fields: First Name, Email, and Password"
      );
      return;
    }

    if (!newUserData.confirmPassword) {
      setError("Please confirm your password");
      return;
    }

    if (newUserData.password !== newUserData.confirmPassword) {
      setError("Password and confirm password do not match");
      return;
    }

    // Validate password strength (min 8 chars, uppercase, lowercase, number, special char)
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(newUserData.password)) {
      setError(
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
      );
      return;
    }

    // Ensure selectedCourse is not null
    const selectedCourseId =
      enquiry?.courseInterest?._id || enquiry?.courseInterest;
    if (!selectedCourseId) {
      setError(
        "Cannot create student account: Enquiry does not have a course interest. Please assign a course to the enquiry first."
      );
      return;
    }

    if (!newUserData.termsAccepted) {
      setError("You must accept the terms and conditions");
      return;
    }

    setConvertLoading(true);
    setError(null);
    try {
      // Parse name from enquiry
      const nameParts = enquiry?.name?.split(" ") || [];
      const firstName = newUserData.firstName || nameParts[0] || "";
      const lastName =
        newUserData.lastName || nameParts.slice(1).join(" ") || "";

      // Create student account with all required fields
      const response = await authApi.post("/auth/signup/student", {
        firstName,
        lastName,
        email: newUserData.email || enquiry?.email,
        password: newUserData.password,
        confirmPassword: newUserData.confirmPassword, // Required by backend
        mobileNumber: newUserData.mobileNumber || enquiry?.phone,
        dateOfBirth: newUserData.dateOfBirth || undefined,
        gender: newUserData.gender || undefined,
        address: newUserData.address || undefined,
        selectedCourse: selectedCourseId, // Required, never null
        termsAccepted: newUserData.termsAccepted, // Required by backend
      });

      const createdUserId = response.data?.user?._id || response.data?._id;
      if (createdUserId) {
        setConvertStudentId(createdUserId);
        setExistingUser(response.data?.user || response.data);
        setCreateUserMode(false);
        // Auto-fill the form with the created user
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to create user account"
      );
    } finally {
      setConvertLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    setStatusLoading(true);
    try {
      const response = await adminApi.patch(`/enquiries/${enquiryId}`, {
        status: newStatus,
      });
      setEnquiry(response.data);
      setShowStatusModal(false);
      loadEnquiry();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleNotesUpdate = async () => {
    setNotesLoading(true);
    try {
      const response = await adminApi.patch(`/enquiries/${enquiryId}`, {
        notes,
      });
      setEnquiry(response.data);
      setShowNotesModal(false);
      loadEnquiry();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update notes");
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAssign = async () => {
    setAssignLoading(true);
    try {
      const response = await adminApi.patch(`/enquiries/${enquiryId}`, {
        assignedTo: assignedTo || null,
      });
      setEnquiry(response.data);
      setShowAssignModal(false);
      loadEnquiry();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to assign enquiry");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAddFollowUp = async () => {
    if (!followUpDate || !followUpNote.trim()) {
      setError("Please provide both date and note for follow-up");
      return;
    }
    setFollowUpLoading(true);
    try {
      const response = await adminApi.patch(`/enquiries/${enquiryId}`, {
        followUp: {
          date: new Date(followUpDate).toISOString(),
          note: followUpNote,
        },
      });
      setEnquiry(response.data);
      setShowFollowUpModal(false);
      setFollowUpDate("");
      setFollowUpNote("");
      loadEnquiry();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add follow-up");
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handlePriorityUpdate = async () => {
    setPriorityLoading(true);
    try {
      const response = await adminApi.patch(`/enquiries/${enquiryId}`, {
        priority,
      });
      setEnquiry(response.data);
      setShowPriorityModal(false);
      loadEnquiry();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update priority");
    } finally {
      setPriorityLoading(false);
    }
  };

  const handleConvertToEnrollment = async () => {
    if (!convertStudentId || !convertBatchId) {
      setError("Please select both student and batch");
      return;
    }
    setConvertLoading(true);
    try {
      // Step 1: Create enrollment
      const enrollmentResponse = await adminApi.post("/enrollments", {
        user: convertStudentId,
        batch: convertBatchId,
        course: enquiry?.courseInterest?._id || enquiry?.courseInterest,
      });

      // Step 2: Update enquiry status to converted
      await adminApi.patch(`/enquiries/${enquiryId}`, {
        status: "converted",
      });

      // Step 3: Navigate to the created enrollment
      const enrollmentId =
        enrollmentResponse.data?.enrollment?._id ||
        enrollmentResponse.data?._id;

      setShowConvertModal(false);
      setConvertStudentId("");
      setConvertBatchId("");
      setExistingUser(null);
      setCreateUserMode(false);

      if (enrollmentId) {
        navigate(`/enrollments/${enrollmentId}`);
      } else {
        navigate("/enrollments");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to convert to enrollment"
      );
    } finally {
      setConvertLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "var(--color-error)";
      case "high":
        return "var(--color-warning)";
      case "medium":
        return "var(--color-primary)";
      case "low":
        return "var(--color-text-tertiary)";
      default:
        return "var(--color-text-secondary)";
    }
  };

  if (loading) {
    return <div className="detail-loading">Loading enquiry details...</div>;
  }

  if (error && !enquiry) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate("/enquiries")}>
          Back to Enquiries
        </Button>
      </div>
    );
  }

  return (
    <div className="enquiry-detail-page">
      <header className="detail-header">
        <Link to="/enquiries" className="back-link">
          <FiArrowLeft size={20} />
          Back to Enquiries
        </Link>
        <div className="header-actions">
          <Button variant="secondary" onClick={() => setShowStatusModal(true)}>
            Update Status
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowPriorityModal(true)}
          >
            Set Priority
          </Button>
          {enquiry?.status !== "converted" && (
            <Button variant="primary" onClick={() => setShowConvertModal(true)}>
              <FiUserCheck size={18} />
              Convert to Enrollment
            </Button>
          )}
        </div>
      </header>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="detail-tabs">
        <button
          className={activeTab === "overview" ? "active" : ""}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={activeTab === "notes" ? "active" : ""}
          onClick={() => setActiveTab("notes")}
        >
          <FiMessageSquare size={16} />
          Notes
        </button>
        <button
          className={activeTab === "followups" ? "active" : ""}
          onClick={() => setActiveTab("followups")}
        >
          <FiClock size={16} />
          Follow-ups
        </button>
        <button
          className={activeTab === "analytics" ? "active" : ""}
          onClick={() => setActiveTab("analytics")}
        >
          <FiTrendingUp size={16} />
          Analytics
        </button>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          {activeTab === "overview" && (
            <>
              <section className="detail-card">
                <div className="card-header">
                  <div className="enquiry-icon">
                    <FiMessageSquare size={32} />
                  </div>
                  <div>
                    <h1>{enquiry?.name || "Enquiry"}</h1>
                    <div className="header-badges">
                      <StatusBadge status={enquiry?.status || "new"} />
                      <span
                        className="priority-badge"
                        style={{
                          color: getPriorityColor(
                            enquiry?.priority || "medium"
                          ),
                        }}
                      >
                        {enquiry?.priority
                          ? enquiry.priority.charAt(0).toUpperCase() +
                            enquiry.priority.slice(1)
                          : "Medium"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <FiMail size={18} />
                    <div>
                      <span className="info-label">Email</span>
                      <strong>{enquiry?.email || "—"}</strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiPhone size={18} />
                    <div>
                      <span className="info-label">Phone</span>
                      <strong>{enquiry?.phone || "—"}</strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiCalendar size={18} />
                    <div>
                      <span className="info-label">Submitted</span>
                      <strong>
                        {enquiry?.createdAt
                          ? new Date(enquiry.createdAt).toLocaleDateString()
                          : "—"}
                      </strong>
                    </div>
                  </div>
                  <div className="info-item">
                    <FiUser size={18} />
                    <div>
                      <span className="info-label">Assigned To</span>
                      {enquiry?.assignedTo ? (
                        <strong>
                          {enquiry.assignedTo.firstName}{" "}
                          {enquiry.assignedTo.lastName}
                        </strong>
                      ) : (
                        <span className="text-muted">Unassigned</span>
                      )}
                    </div>
                  </div>
                  <div className="info-item">
                    <FiAlertCircle size={18} />
                    <div>
                      <span className="info-label">Source</span>
                      <strong>{enquiry?.source || "website"}</strong>
                    </div>
                  </div>
                </div>

                {enquiry?.message && (
                  <div className="message-section">
                    <h2>Message</h2>
                    <p className="message-text">{enquiry.message}</p>
                  </div>
                )}

                {enquiry?.courseInterest && (
                  <div className="course-section">
                    <h2>Interested Course</h2>
                    <Link
                      to={`/courses/${
                        enquiry.courseInterest._id || enquiry.courseInterest
                      }`}
                      className="course-link"
                    >
                      {enquiry.courseInterest.title || "Course"}
                    </Link>
                  </div>
                )}
              </section>
            </>
          )}

          {activeTab === "notes" && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Notes & Comments</h2>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setNotes(enquiry?.notes || "");
                    setShowNotesModal(true);
                  }}
                >
                  <FiEdit size={18} />
                  {enquiry?.notes ? "Edit Notes" : "Add Notes"}
                </Button>
              </div>
              {enquiry?.notes ? (
                <div className="notes-content">
                  <p>{enquiry.notes}</p>
                  <span className="notes-date">
                    Last updated:{" "}
                    {enquiry?.updatedAt
                      ? new Date(enquiry.updatedAt).toLocaleString()
                      : "—"}
                  </span>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No notes added yet. Click "Add Notes" to add comments.</p>
                </div>
              )}
            </section>
          )}

          {activeTab === "followups" && (
            <section className="detail-card">
              <div className="section-header">
                <h2>Follow-up History</h2>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFollowUpDate("");
                    setFollowUpNote("");
                    setShowFollowUpModal(true);
                  }}
                >
                  <FiClock size={18} />
                  Add Follow-up
                </Button>
              </div>
              {enquiry?.followUps && enquiry.followUps.length > 0 ? (
                <div className="followups-list">
                  {enquiry.followUps
                    .sort(
                      (a, b) =>
                        new Date(b.date || b.createdAt) -
                        new Date(a.date || a.createdAt)
                    )
                    .map((followUp, idx) => (
                      <div key={idx} className="followup-item">
                        <div className="followup-marker"></div>
                        <div className="followup-content">
                          <div className="followup-header">
                            <strong>
                              {followUp.date
                                ? new Date(followUp.date).toLocaleDateString()
                                : new Date(
                                    followUp.createdAt
                                  ).toLocaleDateString()}
                            </strong>
                            {followUp.staff && (
                              <span className="followup-staff">
                                by {followUp.staff.firstName}{" "}
                                {followUp.staff.lastName}
                              </span>
                            )}
                          </div>
                          {followUp.note && <p>{followUp.note}</p>}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>
                    No follow-ups recorded yet. Click "Add Follow-up" to track
                    interactions.
                  </p>
                </div>
              )}
            </section>
          )}

          {activeTab === "analytics" && (
            <section className="detail-card">
              <h2>Enquiry Analytics</h2>
              <div className="analytics-grid">
                <div className="analytics-item">
                  <span className="analytics-label">Enquiry Age</span>
                  <strong className="analytics-value">
                    {enquiry?.createdAt
                      ? Math.floor(
                          (new Date() - new Date(enquiry.createdAt)) /
                            (1000 * 60 * 60 * 24)
                        )
                      : 0}{" "}
                    days
                  </strong>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">Status</span>
                  <strong className="analytics-value">
                    <StatusBadge status={enquiry?.status || "new"} />
                  </strong>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">Priority</span>
                  <strong
                    className="analytics-value"
                    style={{
                      color: getPriorityColor(enquiry?.priority || "medium"),
                    }}
                  >
                    {enquiry?.priority
                      ? enquiry.priority.charAt(0).toUpperCase() +
                        enquiry.priority.slice(1)
                      : "Medium"}
                  </strong>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">Follow-ups</span>
                  <strong className="analytics-value">
                    {enquiry?.followUps?.length || 0}
                  </strong>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">Source</span>
                  <strong className="analytics-value">
                    {enquiry?.source || "website"}
                  </strong>
                </div>
                <div className="analytics-item">
                  <span className="analytics-label">Assigned</span>
                  <strong className="analytics-value">
                    {enquiry?.assignedTo ? "Yes" : "No"}
                  </strong>
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="detail-sidebar">
          <section className="detail-card">
            <h3>Quick Actions</h3>
            <div className="action-list">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setShowStatusModal(true)}
              >
                Change Status
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setNotes(enquiry?.notes || "");
                  setShowNotesModal(true);
                }}
              >
                <FiMessageSquare size={16} />
                {enquiry?.notes ? "Edit Notes" : "Add Notes"}
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setAssignedTo(
                    enquiry?.assignedTo?._id || enquiry?.assignedTo || ""
                  );
                  setShowAssignModal(true);
                }}
              >
                <FiUser size={16} />
                Assign Staff
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setFollowUpDate("");
                  setFollowUpNote("");
                  setShowFollowUpModal(true);
                }}
              >
                <FiClock size={16} />
                Add Follow-up
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setPriority(enquiry?.priority || "medium");
                  setShowPriorityModal(true);
                }}
              >
                Set Priority
              </Button>
              {enquiry?.status !== "converted" && (
                <Button
                  variant="primary"
                  fullWidth
                  onClick={() => setShowConvertModal(true)}
                >
                  <FiUserCheck size={16} />
                  Convert to Enrollment
                </Button>
              )}
              {enquiry?.email && (
                <a href={`mailto:${enquiry.email}`}>
                  <Button variant="secondary" fullWidth>
                    <FiMail size={16} />
                    Send Email
                  </Button>
                </a>
              )}
              {enquiry?.phone && (
                <a href={`tel:${enquiry.phone}`}>
                  <Button variant="secondary" fullWidth>
                    <FiPhone size={16} />
                    Call
                  </Button>
                </a>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Enquiry Status"
        size="small"
      >
        <Select
          label="Status"
          name="status"
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value)}
          options={[
            { value: "new", label: "New" },
            { value: "contacted", label: "Contacted" },
            { value: "in-progress", label: "In Progress" },
            { value: "converted", label: "Converted" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleStatusUpdate} loading={statusLoading}>
            Update Status
          </Button>
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title={enquiry?.notes ? "Edit Notes" : "Add Notes"}
        size="medium"
      >
        <FormInput
          label="Notes"
          name="notes"
          type="textarea"
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes or comments about this enquiry..."
        />
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleNotesUpdate} loading={notesLoading}>
            Save Notes
          </Button>
        </div>
      </Modal>

      {/* Assign Staff Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssignedTo("");
        }}
        title="Assign to Staff"
        size="small"
      >
        <Select
          label="Assign To"
          name="assignedTo"
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
          options={[
            { value: "", label: "Unassign" },
            ...staff.map((s) => ({
              value: s._id,
              label: `${s.firstName} ${s.lastName}`.trim() || s.email,
            })),
          ]}
        />
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setShowAssignModal(false);
              setAssignedTo("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAssign} loading={assignLoading}>
            Assign
          </Button>
        </div>
      </Modal>

      {/* Follow-up Modal */}
      <Modal
        isOpen={showFollowUpModal}
        onClose={() => {
          setShowFollowUpModal(false);
          setFollowUpDate("");
          setFollowUpNote("");
        }}
        title="Add Follow-up"
        size="medium"
      >
        <FormInput
          label="Follow-up Date"
          name="followUpDate"
          type="datetime-local"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
          required
        />
        <FormInput
          label="Follow-up Note"
          name="followUpNote"
          type="textarea"
          rows={4}
          value={followUpNote}
          onChange={(e) => setFollowUpNote(e.target.value)}
          placeholder="Enter follow-up details..."
          required
        />
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setShowFollowUpModal(false);
              setFollowUpDate("");
              setFollowUpNote("");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleAddFollowUp} loading={followUpLoading}>
            Add Follow-up
          </Button>
        </div>
      </Modal>

      {/* Priority Modal */}
      <Modal
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        title="Set Priority"
        size="small"
      >
        <Select
          label="Priority"
          name="priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          options={[
            { value: "low", label: "Low" },
            { value: "medium", label: "Medium" },
            { value: "high", label: "High" },
            { value: "urgent", label: "Urgent" },
          ]}
        />
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setShowPriorityModal(false)}
          >
            Cancel
          </Button>
          <Button onClick={handlePriorityUpdate} loading={priorityLoading}>
            Update Priority
          </Button>
        </div>
      </Modal>

      {/* Convert to Enrollment Modal */}
      <Modal
        isOpen={showConvertModal}
        onClose={() => {
          setShowConvertModal(false);
          setConvertStudentId("");
          setConvertBatchId("");
          setCreateUserMode(false);
          setStudentSearchQuery("");
        }}
        title="Convert to Enrollment"
        size="large"
      >
        <div className="convert-form">
          <p className="convert-info">
            This will create a new enrollment for this enquiry. The enquiry
            status will be updated to "Converted".
          </p>

          {/* Enquiry Contact Info */}
          {enquiry && (
            <div className="enquiry-contact-card">
              <h3>Enquiry Contact Information</h3>
              <div className="contact-info">
                <p>
                  <strong>Name:</strong> {enquiry.name}
                </p>
                <p>
                  <strong>Email:</strong> {enquiry.email}
                </p>
                <p>
                  <strong>Phone:</strong> {enquiry.phone}
                </p>
                {enquiry.courseInterest && (
                  <p>
                    <strong>Interested Course:</strong>{" "}
                    {enquiry.courseInterest.title || "Course"}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Student Selection/Creation */}
          <div className="convert-step">
            <h3>Step 1: Student Account</h3>
            {checkingUser && (
              <p className="convert-note">
                Checking for existing user account...
              </p>
            )}
            {existingUser && !createUserMode && (
              <div className="existing-user-card">
                <p className="success-message">
                  ✓ Found existing student account:{" "}
                  <strong>
                    {existingUser.firstName} {existingUser.lastName}
                  </strong>{" "}
                  ({existingUser.email})
                </p>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setExistingUser(null);
                    setConvertStudentId("");
                  }}
                >
                  Use Different Student
                </Button>
              </div>
            )}
            {!existingUser && !createUserMode && (
              <>
                <div className="student-search-section">
                  <FormInput
                    label="Search Student (by name, email, or phone)"
                    name="studentSearch"
                    value={studentSearchQuery}
                    onChange={(e) => setStudentSearchQuery(e.target.value)}
                    placeholder="Search for existing student..."
                  />
                  <Select
                    label="Select Existing Student"
                    name="student"
                    value={convertStudentId}
                    onChange={(e) => setConvertStudentId(e.target.value)}
                    options={[
                      { value: "", label: "Select a student" },
                      ...students.map((s) => ({
                        value: s._id,
                        label: `${s.firstName} ${s.lastName}`.trim() || s.email,
                      })),
                    ]}
                  />
                </div>
                <div className="create-user-option">
                  <p className="convert-note">
                    Can't find the student?{" "}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setCreateUserMode(true);
                        // Pre-fill form with enquiry data
                        const nameParts = enquiry?.name?.split(" ") || [];
                        setNewUserData({
                          firstName: nameParts[0] || "",
                          lastName: nameParts.slice(1).join(" ") || "",
                          email: enquiry?.email || "",
                          password: "",
                          confirmPassword: "",
                          mobileNumber: enquiry?.phone || "",
                          dateOfBirth: "",
                          gender: "",
                          address: "",
                          termsAccepted: true,
                        });
                      }}
                    >
                      Create new student account from enquiry
                    </button>
                  </p>
                </div>
              </>
            )}
            {createUserMode && (
              <div className="create-user-form">
                <h4>Create New Student Account</h4>
                <div className="form-grid">
                  <FormInput
                    label="First Name *"
                    name="firstName"
                    value={newUserData.firstName}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        firstName: e.target.value,
                      })
                    }
                    required
                  />
                  <FormInput
                    label="Last Name"
                    name="lastName"
                    value={newUserData.lastName}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        lastName: e.target.value,
                      })
                    }
                  />
                  <FormInput
                    label="Email *"
                    name="email"
                    type="email"
                    value={newUserData.email}
                    onChange={(e) =>
                      setNewUserData({ ...newUserData, email: e.target.value })
                    }
                    required
                  />
                  <FormInput
                    label="Password *"
                    name="password"
                    type="password"
                    value={newUserData.password}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        password: e.target.value,
                      })
                    }
                    required
                    placeholder="Min 8 chars, uppercase, lowercase, number, special char"
                  />
                  <FormInput
                    label="Confirm Password *"
                    name="confirmPassword"
                    type="password"
                    value={newUserData.confirmPassword}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        confirmPassword: e.target.value,
                      })
                    }
                    required
                    placeholder="Re-enter password"
                  />
                  <FormInput
                    label="Phone Number *"
                    name="mobileNumber"
                    value={newUserData.mobileNumber}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        mobileNumber: e.target.value,
                      })
                    }
                    required
                    placeholder="10-digit Indian mobile number"
                  />
                  <FormInput
                    label="Date of Birth"
                    name="dateOfBirth"
                    type="date"
                    value={newUserData.dateOfBirth}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        dateOfBirth: e.target.value,
                      })
                    }
                  />
                  <Select
                    label="Gender"
                    name="gender"
                    value={newUserData.gender}
                    onChange={(e) =>
                      setNewUserData({ ...newUserData, gender: e.target.value })
                    }
                    options={[
                      { value: "", label: "Select gender" },
                      { value: "Male", label: "Male" },
                      { value: "Female", label: "Female" },
                      { value: "Other", label: "Other" },
                    ]}
                  />
                  <FormInput
                    label="Address"
                    name="address"
                    type="textarea"
                    rows={2}
                    value={newUserData.address}
                    onChange={(e) =>
                      setNewUserData({
                        ...newUserData,
                        address: e.target.value,
                      })
                    }
                  />
                  <div className="terms-acceptance">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newUserData.termsAccepted}
                        onChange={(e) =>
                          setNewUserData({
                            ...newUserData,
                            termsAccepted: e.target.checked,
                          })
                        }
                        required
                      />
                      <span>I accept the terms and conditions *</span>
                    </label>
                  </div>
                </div>
                {enquiry?.courseInterest && (
                  <div className="course-info-note">
                    <p>
                      <strong>Course:</strong>{" "}
                      {enquiry.courseInterest.title || "Course"} will be
                      automatically assigned to this student.
                    </p>
                  </div>
                )}
                {!enquiry?.courseInterest && (
                  <div className="course-warning-note">
                    <p>
                      ⚠️ <strong>Warning:</strong> This enquiry does not have a
                      course interest. You must assign a course to the enquiry
                      before creating a student account.
                    </p>
                  </div>
                )}
                <div className="form-actions-inline">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCreateUserMode(false);
                      setNewUserData({
                        firstName: "",
                        lastName: "",
                        email: "",
                        password: "",
                        confirmPassword: "",
                        mobileNumber: "",
                        dateOfBirth: "",
                        gender: "",
                        address: "",
                        termsAccepted: true,
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateUserFromEnquiry}
                    loading={convertLoading}
                  >
                    Create Student Account
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Batch Selection */}
          {convertStudentId && (
            <div className="convert-step">
              <h3>Step 2: Select Batch</h3>
              {enquiry?.courseInterest && batches.length > 0 ? (
                <Select
                  label="Select Batch"
                  name="batch"
                  value={convertBatchId}
                  onChange={(e) => setConvertBatchId(e.target.value)}
                  options={[
                    { value: "", label: "Select a batch" },
                    ...batches.map((b) => ({
                      value: b._id,
                      label: `${b.batchName} (${b.status}) - ${
                        b.course &&
                        typeof b.course === "object" &&
                        b.course.title
                          ? b.course.title
                          : "Course"
                      }`,
                    })),
                  ]}
                  required
                />
              ) : (
                <p className="convert-note error-note">
                  No batches available for the interested course. Please create
                  a batch first.
                </p>
              )}
            </div>
          )}

          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setShowConvertModal(false);
                setConvertStudentId("");
                setConvertBatchId("");
                setCreateUserMode(false);
                setStudentSearchQuery("");
                setExistingUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConvertToEnrollment}
              loading={convertLoading}
              disabled={!convertStudentId || !convertBatchId}
            >
              <FiUserCheck size={18} />
              Convert to Enrollment
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EnquiryDetail;
